// cSpell:ignore afrekening Correctie eindafrekening einde huurcontract periode privé volgende
// A lease end that moved after a confirmed settlement settled the lease up
// to its old end (decision 3, Lewis 2026-10-20; M01, M05, M06, M07 of the
// retest of 20/10): the next settlement of the unit or the building gets a
// correction line for the tenant and, the other way round, for the owner of
// the unit, so neither the tenant's balance nor the owner's vacancy stays
// wrong. "Correctie eindafrekening <periode>" after a departure, "Correctie
// einde huurcontract <periode>" for a lease that never had one.
//
// Every confirmed settlement freezes the lease end it settled a lease up to
// (a departure its `until`, any other settlement its rows' `leaseEnd`), and
// every correction the days it charged or gave back, per cost and per
// provision slice. Per lease and cost, the days charged to the tenant are
// what those settlements and corrections add up to, so a correction always
// covers only what is not charged yet, in either direction, however often
// the lease end moves:
// - The end moved later: the days after the last day charged, of a cost
//   another confirmed settlement settled, are charged to the tenant here and
//   credited to the owner. A cost this settlement takes charges them by its
//   own split already. The provision of those days counts as usual inside
//   this settlement's period (D7); before it (a correction landing in next
//   year's settlement), the correction credits it, and records those months
//   as settled.
// - The end moved earlier: the days after the new end that were charged, of
//   any settled cost, go back to the tenant, and the provision counted for
//   them is paid back; the owner carries both, the way their vacancy would
//   have gone.
//
// The amounts are the settlements' own frozen figures, prorated by days for
// a cost and by the lease's provision schedule for the provision, in whole
// cents.

import type { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { fromCents, toCents } from "./money.utils";
import type { Settlement } from "./types";

const PREFIX = "correction:";
const PRIVATE_SUFFIX = " (privé)";

// The id a correction of a lease without a departure goes by (in its
// breakdown key and IDepartureCorrection.settlementId).
const LEASE_PREFIX = "lease-";
export const leaseCorrectionId = (contractId: string): string =>
  `${LEASE_PREFIX}${contractId}`;

// The breakdown key of one lease's correction in one kind: the days it
// corrects are in it, so every screen and the statement can name it.
export const departureCorrectionKey = (params: {
  settlementId: string;
  from: string;
  to: string;
  isPrivate: boolean;
}): string =>
  `${PREFIX}${params.from}:${params.to}:${params.settlementId}${
    params.isPrivate ? PRIVATE_SUFFIX : ""
  }`;

export const isDepartureCorrectionKey = (key: string): boolean =>
  key.startsWith(PREFIX);

export const parseDepartureCorrectionKey = (
  key: string,
): { from: string; to: string; settlementId: string } | undefined => {
  if (!isDepartureCorrectionKey(key)) return undefined;
  const body = key
    .slice(PREFIX.length)
    .replace(new RegExp(`${PRIVATE_SUFFIX.replace(/[()]/g, "\\$&")}$`), "");
  const [from, to, ...rest] = body.split(":");
  if (!from || !to || rest.length === 0) return undefined;
  return { from, to, settlementId: rest.join(":") };
};

const dayText = (iso: string) => {
  const [year, month, day] = iso.slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
};

// "Correctie eindafrekening 01/06/2026 – 30/06/2026" (after a departure) or
// "Correctie einde huurcontract …" (a lease without one), in `locale` when
// given (a statement's recipient), else in the user's language.
export const departureCorrectionLabel = (
  key: string,
  locale?: ELocale,
): string | undefined => {
  const parsed = parseDepartureCorrectionKey(key);
  if (!parsed) return undefined;
  return getLocalizedText(
    parsed.settlementId.startsWith(LEASE_PREFIX)
      ? "settlement.lease_end_correction.label"
      : "settlement.departure_correction.label",
    { from: dayText(parsed.from), to: dayText(parsed.to) },
    0,
    locale,
  );
};

// A day range, both days included (ISO dates).
export interface ICorrectionDays {
  from: string;
  to: string;
}

export type TCorrectionSliceDays = Partial<
  Record<"common" | "private", ICorrectionDays[]>
>;

// What a correction did with one cost: the days it charged (sign 1) or gave
// back (sign -1), and that amount excl. VAT and its VAT (signed, + the
// tenant pays).
export interface IDepartureCorrectionInvoice {
  sign: 1 | -1;
  days: ICorrectionDays[];
  amount: number;
  vat: number;
}

// What a settlement corrected of one lease whose end moved (frozen on
// confirm).
export interface IDepartureCorrection {
  // The departure corrected, or leaseCorrectionId(contractId) for a lease
  // without one.
  settlementId: string;
  contractId: string;
  unitId: string;
  tenantName: string;
  // The last day the lease was settled up to before, and the lease's end it
  // was corrected to.
  until: string;
  end: string;
  // The days corrected (the first and the last of them).
  from: string;
  to: string;
  // + the tenant pays, - they get back; excl. VAT, its VAT, and the part of
  // the amount that is provision (+ paid back, - credited).
  amount: number;
  vat: number;
  provision: number;
  // The costs it corrected.
  invoiceIds: string[];
  provisionCorrected: boolean;
  // Per cost, the days it charged or gave back. Absent on the corrections
  // of the first version (2026-10-20 morning): their costs count over
  // from–to, charged when end > until.
  byInvoice?: Record<string, IDepartureCorrectionInvoice>;
  // Per provision slice, the days whose provision it credited (they count
  // as settled from then on) and those it took back (open again).
  provisionCreditedBySlice?: TCorrectionSliceDays;
  provisionTakenBackBySlice?: TCorrectionSliceDays;
}

// The days of one cost a correction charged (+1) or gave back (-1).
export const correctionDaysOf = (
  correction: IDepartureCorrection,
  invoiceId: string,
): { sign: 1 | -1; days: ICorrectionDays[] } | undefined => {
  const own = correction.byInvoice?.[invoiceId];
  if (own) return { sign: own.sign, days: own.days };
  if (correction.byInvoice || !correction.invoiceIds.includes(invoiceId)) {
    return undefined;
  }
  return {
    sign: correction.end > correction.until ? 1 : -1,
    days: [{ from: correction.from, to: correction.to }],
  };
};

// The costs a correction touched.
export const correctionInvoiceIds = (
  correction: IDepartureCorrection,
): string[] =>
  correction.byInvoice
    ? Object.keys(correction.byInvoice)
    : correction.invoiceIds;

// One row of a confirmed settlement, as a later settlement reads it to
// correct a lease end that moved: the lease end it settled the lease up to,
// its share per cost and the provision days it counted.
export interface ISettledLeaseRow {
  settlementId: string;
  contractId: string;
  isDeparture: boolean;
  // A departure: its last day. Any other settlement: PerContractResult
  // .leaseEnd, else (older snapshots) the end on the tenant's statement.
  // Undefined when not known.
  leaseEnd?: string;
  // The costs the settlement settled.
  invoiceIds: string[];
  costByInvoice: Record<string, number>;
  vatByInvoice: Record<string, number>;
  provisionIntervalsBySlice?: TCorrectionSliceDays;
}

// The rows of every confirmed settlement but `ownId` (see ISettledLeaseRow).
export const settledLeaseRows = (params: {
  settlements: Settlement[];
  ownId?: string;
}): ISettledLeaseRow[] =>
  params.settlements.flatMap(settlement => {
    const { snapshot } = settlement;
    if (
      settlement.status !== "confirmed" ||
      settlement.id === params.ownId ||
      !snapshot
    ) {
      return [];
    }
    const { departure } = snapshot;
    return (snapshot.perContract ?? []).flatMap(row => {
      if (departure && row.contractId !== departure.contractId) return [];
      const leaseEnd = departure
        ? departure.until
        : row.leaseEnd ??
          snapshot.statements?.[`tenant:${row.contractId}`]?.contractEnd;
      const flat = row.provisionIntervals;
      // An older row froze its days for every slice at once.
      const flatBySlice = flat ? { common: flat, private: flat } : undefined;
      const bySlice = row.provisionIntervalsBySlice ?? flatBySlice;
      return [
        {
          settlementId: settlement.id,
          contractId: row.contractId,
          isDeparture: !!departure,
          ...(leaseEnd ? { leaseEnd: leaseEnd.slice(0, 10) } : {}),
          invoiceIds: snapshot.invoiceIds ?? [],
          costByInvoice: row.costByInvoice ?? {},
          vatByInvoice: row.vatByInvoice ?? {},
          ...(bySlice ? { provisionIntervalsBySlice: bySlice } : {}),
        },
      ];
    });
  });

// The corrections of a departure whose lease end moved, as confirmed
// settlements (and the settlement being made or shown) froze them, by the
// name of the settlement that made each (M28).
export interface ICorrectionsBySettlement {
  settlementId: string;
  name: string;
  corrections: IDepartureCorrection[];
}

// Where a departure's moved lease end was corrected (M28): once a
// correction brought the lease up to its end as it is now, the departure
// and the settlements that listed it say so, by name and with the net
// amount of those corrections (excl. VAT and its VAT, + the tenant paid,
// - they got back), instead of promising that "de volgende afrekening"
// corrects it. `isOwn` when the settlement shown made the latest of them.
// Undefined while no correction reached the current end.
export const leaseEndCorrectedIn = (params: {
  departureSettlementId: string;
  leaseEnd: string;
  bySettlement: ICorrectionsBySettlement[];
  ownId?: string;
}):
  | {
      settlementNames: string[];
      amount: number;
      vat: number;
      isOwn: boolean;
    }
  | undefined => {
  const leaseEnd = params.leaseEnd.slice(0, 10);
  const made = params.bySettlement.flatMap(settlement =>
    settlement.corrections
      .filter(
        correction => correction.settlementId === params.departureSettlementId,
      )
      .map(correction => ({ settlement, correction })),
  );
  const reaching = made.filter(
    ({ correction }) => correction.end.slice(0, 10) === leaseEnd,
  );
  if (reaching.length === 0) return undefined;
  return {
    settlementNames: [
      ...new Set(made.map(({ settlement }) => settlement.name)),
    ],
    amount: fromCents(
      made.reduce((sum, { correction }) => sum + toCents(correction.amount), 0),
    ),
    vat: fromCents(
      made.reduce((sum, { correction }) => sum + toCents(correction.vat), 0),
    ),
    isOwn: reaching.some(
      ({ settlement }) => settlement.settlementId === params.ownId,
    ),
  };
};

// The corrections each confirmed settlement made, by its name.
export const confirmedCorrectionsBySettlement = (params: {
  settlements: Settlement[];
  nameOf: (settlement: Settlement) => string;
}): ICorrectionsBySettlement[] =>
  params.settlements.flatMap(settlement =>
    settlement.status === "confirmed" &&
    (settlement.snapshot?.departureCorrections ?? []).length > 0
      ? [
          {
            settlementId: settlement.id,
            name: params.nameOf(settlement),
            corrections: settlement.snapshot?.departureCorrections ?? [],
          },
        ]
      : [],
  );
